import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, Snowflake, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import ProfileLink from '../../../components/ProfileLink'
import { useTeamStore } from '../store/useTeamStore'
import { personName, secondsUntil, teamErrorText } from '../utils/teamFormat'
import { formatSecondsLeft } from '../../../utils/formatters'
import { hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

/**
 * Плашки нерабочих состояний банды (видны только участникам):
 *
 * - `delete` — идёт таймер роспуска. `deletes_at` приходит в unixtime
 *   (единственное исключение из «секунд назад»), из него крутится таймер.
 *   Фактический роспуск делает воркер, поэтому он может наступить на несколько
 *   минут позже нуля — на нуле показываем «удаляется».
 * - `frozen` — банду заморозила администрация, доступен только выход.
 *
 * Если за время таймера банда опустела, бэкенд присылает создателю
 * `delete_instant: true` — тогда рядом с отменой появляется «Распустить
 * сейчас»: досиживать остаток таймера незачем. Отдельной ручки нет, это тот же
 * `POST /api/team/delete/`.
 */
const TeamDeleteNotice = ({ team }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { cancelDelete, startDelete, isMutating } = useTeamStore()
  const deletesAt = team.delete?.deletes_at
  const [left, setLeft] = useState(() => secondsUntil(deletesAt))

  useEffect(() => {
    if (!deletesAt) return
    setLeft(secondsUntil(deletesAt))
    const timer = setInterval(() => setLeft(secondsUntil(deletesAt)), 1000)
    return () => clearInterval(timer)
  }, [deletesAt])

  const handleCancel = async () => {
    const res = await cancelDelete()
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-delete-cancel')
      toast.success(t('team.deleteCancelled', 'Удаление отменено'))
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // Критерий мгновенного роспуска считает бэкенд: если к моменту нажатия он
  // перестал выполняться, вернётся `disbanded: false` и таймер просто останется
  const handleDisbandNow = async () => {
    const res = await startDelete()
    if (res?.ok) {
      hapticNotify('warning')
      if (res.disbanded) {
        sendYandexGoal('team-delete-instant')
        toast.success(t('team.disbanded', 'Банда распущена'))
        navigate('/teams', { replace: true })
      }
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  if (team.delete) {
    return (
      <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-500/20">
            <AlertTriangle size={20} className="text-red-400" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-bold not-italic text-red-300">
              {t('team.deletingTitle', 'Банда удаляется')}
            </p>
            <p className="mt-1 text-xs text-red-200/80">
              {left > 0
                ? t('team.deletingIn', 'До роспуска: {{time}}. Всё это время банда заморожена — доступен только выход участников и отмена удаления.', { time: formatSecondsLeft(left, t) })
                : t('team.deletingNow', 'Роспуск уже начался, банда исчезнет в течение нескольких минут.')}
            </p>
            {team.delete.initiator && (
              <p className="mt-1.5 text-xs text-red-200/60">
                {t('team.deletingBy', 'Запустил:')}{' '}
                <ProfileLink slug={team.delete.initiator.slug} className="font-medium text-red-200">
                  {personName(team.delete.initiator, t)}
                </ProfileLink>
              </p>
            )}

            {team.my_role === 'owner' && (
              <>
                <button
                  onClick={handleCancel}
                  disabled={isMutating}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-2.5 text-sm font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  {isMutating && <Loader2 size={15} className="animate-spin" />}
                  {t('team.cancelDelete', 'Отменить удаление')}
                </button>
                {/* Строго `=== true`: у не-создателя в delete_instant приходит null */}
                {team.delete_instant === true && (
                  <button
                    onClick={handleDisbandNow}
                    disabled={isMutating}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm font-semibold text-red-300 transition-all active:scale-[0.98] disabled:opacity-50"
                  >
                    <Trash2 size={15} />
                    {t('team.disbandNow', 'Распустить сейчас')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (team.frozen || team.status === 'frozen') {
    return (
      <div className="rounded-2xl border border-info/40 bg-info/10 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-info/20">
            <Snowflake size={20} className="text-info" />
          </span>
          <div>
            <p className="font-bold not-italic text-info">
              {t('team.frozenTitle', 'Банда заморожена')}
            </p>
            <p className="mt-1 text-xs text-info/80">
              {t('team.frozenDescription', 'Заморозку наложила администрация — доступен только выход из банды. Снять её может тоже только администрация.')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default TeamDeleteNotice
